import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, fmtDate, fmtErr, imgUrl } from "@/api";
import { useLang } from "@/i18n";
import ImagePicker from "@/components/ImagePicker";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const empty = { title: "", category: "berita", content: "", cover: [] };
const CATS = ["berita", "karya-ilmiah", "opini", "gagasan", "kalam-masyayikh"];

export default function ArtikelTab({ user }) {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/articles").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const mine = user.role === "admin" ? items : items.filter((a) => a.author_id === user.id);

  const open = (item) => {
    setForm(item ? { title: item.title, category: item.category, content: item.content, cover: item.cover ? [item.cover] : [] } : empty);
    setDlg(item || "new");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { title: form.title, category: form.category, content: form.content, cover: form.cover[0] || "" };
    try {
      if (dlg === "new") await api.post("/articles", payload);
      else await api.put(`/articles/${dlg.id}`, payload);
      toast.success(t("common.saved"));
      setDlg(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm_delete"))) return;
    try {
      await api.delete(`/articles/${id}`);
      toast.success(t("common.deleted"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="artikel-tab">
      <Button onClick={() => open(null)} className="rounded-full bg-emerald-800 hover:bg-emerald-900 mb-6" data-testid="artikel-add-button">
        <Plus className="h-4 w-4 mr-2" /> {t("common.add")}
      </Button>
      <div className="space-y-3">
        {mine.map((a) => (
          <div key={a.id} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-4 flex items-center gap-4" data-testid={`artikel-item-${a.id}`}>
            {a.cover && <img src={imgUrl(a.cover)} alt="" className="h-16 w-24 object-cover rounded-md hidden sm:block" />}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-emerald-950 truncate">{a.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                <span className="uppercase font-bold text-[#D4AF37]">{a.category.replace("-", " ")}</span> · {fmtDate(a.created_at, lang)} · {a.author_name}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => open(a)} data-testid={`artikel-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="destructive" onClick={() => remove(a.id)} data-testid={`artikel-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
        {mine.length === 0 && <p className="text-sm text-slate-500">{t("galeri.empty")}</p>}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-emerald-950">{t("dash.artikel")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="artikel-form">
            <div>
              <Label className="text-sm font-medium text-slate-700">Judul</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1.5" data-testid="artikel-form-title" />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="artikel-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATS.map((c) => <SelectItem key={c} value={c} data-testid={`artikel-cat-${c}`}>{c.replace("-", " ").toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Sampul ({t("common.optional")})</Label>
              <div className="mt-1.5"><ImagePicker values={form.cover} onChange={(cover) => setForm({ ...form, cover })} max={1} testId="artikel-cover-picker" /></div>
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700">Isi Artikel</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={10} className="mt-1.5" data-testid="artikel-form-content" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900" data-testid="artikel-form-submit">
              {busy ? t("common.loading") : t("common.save")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
